import api from './api';
import { AdminAction, AdminStats, AdminUser, Subscription, UsageStats } from '@/types';

// ---------- User subscription ----------

export const subscriptionService = {
  getMySubscription: async (): Promise<Subscription> => {
    const { data } = await api.get('/subscriptions/');
    return data;
  },

  createCheckoutSession: async (returnUrl: string): Promise<{ url: string }> => {
    const { data } = await api.post('/subscriptions/checkout', { return_url: returnUrl });
    return data;
  },

  createPortalSession: async (returnUrl: string): Promise<{ url: string }> => {
    const { data } = await api.post('/subscriptions/portal', { return_url: returnUrl });
    return data;
  },

  cancelSubscription: async (): Promise<Subscription> => {
    const { data } = await api.post('/subscriptions/cancel');
    return data;
  },

  getUsage: async (): Promise<UsageStats> => {
    const { data } = await api.get('/subscriptions/usage');
    return data;
  },
};

// ---------- Admin ----------

export const adminService = {
  listUsers: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sub_status?: string;
    sort_by?: 'activity' | 'requests' | '';
  }): Promise<AdminUser[]> => {
    const { data } = await api.get('/admin/users', { params });
    return data;
  },

  getUser: async (userId: string): Promise<AdminUser> => {
    const { data } = await api.get(`/admin/users/${userId}`);
    return data;
  },

  grantSubscription: async (userId: string, months: number, notes?: string): Promise<Subscription> => {
    const { data } = await api.post(`/admin/users/${userId}/grant-subscription`, { months, notes });
    return data;
  },

  startTrial: async (userId: string): Promise<Subscription> => {
    const { data } = await api.post(`/admin/users/${userId}/start-trial`);
    return data;
  },

  revokeTrial: async (userId: string): Promise<Subscription> => {
    const { data } = await api.post(`/admin/users/${userId}/revoke-trial`);
    return data;
  },

  cancelSubscription: async (userId: string, notes?: string): Promise<Subscription> => {
    const { data } = await api.post(`/admin/users/${userId}/cancel-subscription`, null, {
      params: notes ? { notes } : undefined,
    });
    return data;
  },

  banUser: async (userId: string, reason: string, expiresAt?: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/admin/users/${userId}/ban`, {
      reason,
      expires_at: expiresAt || null,
    });
    return data;
  },

  unbanUser: async (userId: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/admin/users/${userId}/unban`);
    return data;
  },

  getActions: async (targetUserId?: string, page = 1, size = 50): Promise<AdminAction[]> => {
    const { data } = await api.get('/admin/actions', {
      params: { target_user_id: targetUserId, page, size },
    });
    return data;
  },

  getStats: async (): Promise<AdminStats> => {
    const { data } = await api.get('/admin/stats');
    return data;
  },
};
